// Calendar grid rendered inside the DatePicker popover.
// Pattern: ‹ [Month ▾] [Year ▾] › header, then 7-column day grid.
// Selected day: #263238 (dark gray). Today: #E7EAF0 (light gray). Brand green is NOT used here.

import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { buildMonthGrid, DOW_LETTERS, isSameDay, MONTH_NAMES, parseIso, toIso } from "./dateUtils";

interface CalendarGridProps {
  selectedIso: string;
  onSelect: (iso: string) => void;
  minYear: number;
  maxYear: number;
}

export default function CalendarGrid({ selectedIso, onSelect, minYear, maxYear }: CalendarGridProps) {
  const today = new Date();
  const parsed = selectedIso ? parseIso(selectedIso) : null;
  const [viewMonth, setViewMonth] = useState<number>((parsed ?? today).getMonth());
  const [viewYear, setViewYear] = useState<number>((parsed ?? today).getFullYear());
  const [monthOpen, setMonthOpen] = useState(false);
  const [yearOpen, setYearOpen] = useState(false);

  const monthRef = useRef<HTMLDivElement>(null);
  const yearRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!monthRef.current?.contains(t)) setMonthOpen(false);
      if (!yearRef.current?.contains(t)) setYearOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const grid = buildMonthGrid(viewYear, viewMonth);

  const prevMonth = () => {
    if (viewMonth === 0) {
      if (viewYear > minYear) { setViewYear(viewYear - 1); setViewMonth(11); }
    } else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      if (viewYear < maxYear) { setViewYear(viewYear + 1); setViewMonth(0); }
    } else setViewMonth(viewMonth + 1);
  };

  const yearList: number[] = [];
  for (let y = maxYear; y >= minYear; y--) yearList.push(y);

  return (
    <div className="w-[280px] bg-white border border-line rounded-xl shadow-[0_12px_32px_rgba(14,26,26,0.14)] p-3">
      <div className="flex items-center justify-between mb-2">
        <button type="button" onClick={prevMonth} aria-label="Previous month"
          className="w-7 h-7 border border-line rounded-md bg-white cursor-pointer text-ink3 inline-flex items-center justify-center hover:bg-panel">
          <ChevronLeft size={14} />
        </button>
        <div className="inline-flex gap-1.5">
          <div className="relative" ref={monthRef}>
            <button type="button" onClick={() => { setMonthOpen((v) => !v); setYearOpen(false); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-line bg-white font-bold text-[13px] text-ink cursor-pointer hover:bg-panel">
              {MONTH_NAMES[viewMonth]} <ChevronDown size={10} className="text-ink4" />
            </button>
            {monthOpen && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-line rounded-md shadow-lg z-10 py-1 w-[140px] max-h-[220px] overflow-y-auto">
                {MONTH_NAMES.map((m, i) => (
                  <button key={m} type="button" onClick={() => { setViewMonth(i); setMonthOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-[12px] cursor-pointer bg-transparent border-none hover:bg-panel ${i === viewMonth ? "font-bold text-ink" : "text-ink2"}`}>
                    {m}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative" ref={yearRef}>
            <button type="button" onClick={() => { setYearOpen((v) => !v); setMonthOpen(false); }}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-line bg-white font-bold text-[13px] text-ink cursor-pointer hover:bg-panel">
              {viewYear} <ChevronDown size={10} className="text-ink4" />
            </button>
            {yearOpen && (
              <div className="absolute top-full left-0 mt-1 bg-white border border-line rounded-md shadow-lg z-10 py-1 w-[90px] max-h-[220px] overflow-y-auto">
                {yearList.map((y) => (
                  <button key={y} type="button" onClick={() => { setViewYear(y); setYearOpen(false); }}
                    className={`w-full text-left px-3 py-1.5 text-[12px] cursor-pointer bg-transparent border-none hover:bg-panel ${y === viewYear ? "font-bold text-ink" : "text-ink2"}`}>
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button type="button" onClick={nextMonth} aria-label="Next month"
          className="w-7 h-7 border border-line rounded-md bg-white cursor-pointer text-ink3 inline-flex items-center justify-center hover:bg-panel">
          <ChevronRight size={14} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-[11px]">
        {DOW_LETTERS.map((d, i) => (
          <div key={i} className="text-center font-bold text-ink3 py-1.5 text-[10.5px] tracking-wider">{d}</div>
        ))}
        {grid.map((cell, i) => {
          const isSelected = !!(parsed && isSameDay(cell.date, parsed));
          const isToday = isSameDay(cell.date, today);
          const classes = isSelected
            ? "bg-[#263238] text-white font-bold"
            : isToday
            ? "bg-[#E7EAF0] text-ink font-bold"
            : cell.otherMonth
            ? "text-ink4"
            : "text-ink2";
          return (
            <button key={i} type="button" onClick={() => onSelect(toIso(cell.date))}
              className={`text-center py-1.5 rounded-md cursor-pointer bg-transparent border-none hover:bg-panel ${classes}`}>
              {cell.date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}